import { useEffect, useState, useRef } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';

export function useTaskTimer() {
  const [activeTimers, setActiveTimers] = useState({});
  const [timerDisplays, setTimerDisplays] = useState({});

  // Load active timers
  const loadTimers = async () => {
    try {
      const res = await api.get('/timers/active');
      const timerMap = {};
      res.data.forEach(t => {
        timerMap[t.task_id] = {
          started_at: t.started_at,
          status: t.status || 'running',
          accumulated_seconds: t.accumulated_seconds || 0,
        };
      });
      setActiveTimers(timerMap);
    } catch { /* silent */ }
  };

  // Timer display updater
  useEffect(() => {
    const interval = setInterval(() => {
      const displays = {};
      Object.entries(activeTimers).forEach(([taskId, timer]) => {
        let totalSeconds = timer.accumulated_seconds || 0;
        if (timer.status === 'running') {
          totalSeconds += (Date.now() - new Date(timer.started_at).getTime()) / 1000;
        }
        totalSeconds = Math.floor(totalSeconds);
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;
        displays[taskId] = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
      });
      setTimerDisplays(displays);
    }, 1000);
    return () => clearInterval(interval);
  }, [activeTimers]);

  const startTimer = async (taskId) => {
    try {
      const res = await api.post(`/tasks/${taskId}/timer/start`);
      setActiveTimers(prev => {
        const next = {};
        Object.entries(prev).forEach(([id, t]) => {
          if (id !== taskId && t.status === 'running') next[id] = { ...t, status: 'paused' };
          else if (id !== taskId) next[id] = t;
        });
        next[taskId] = { started_at: res.data.started_at, status: 'running', accumulated_seconds: res.data.accumulated_seconds || 0 };
        return next;
      });
      toast.success(res.data.message || 'Timer started');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to start timer'); }
  };

  const pauseTimer = async (taskId) => {
    try {
      const res = await api.post(`/tasks/${taskId}/timer/pause`);
      setActiveTimers(prev => ({ ...prev, [taskId]: { ...prev[taskId], status: 'paused', accumulated_seconds: res.data.accumulated_seconds } }));
      toast.success('Timer paused');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to pause timer'); }
  };

  const stopTimer = async (taskId) => {
    try {
      const res = await api.post(`/tasks/${taskId}/timer/stop`);
      setActiveTimers(prev => { const n = { ...prev }; delete n[taskId]; return n; });
      toast.success(`Timer stopped: ${res.data.hours}h logged`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to stop timer'); }
  };

  return { activeTimers, setActiveTimers, timerDisplays, startTimer, pauseTimer, stopTimer, loadTimers };
}
